import { useState, useEffect } from 'react';

// Cache for template records to avoid repeated fetches
let templateCache = null;
let templateCachePromise = null;

const NONE_LABEL = 'None';

/**
 * @param {unknown} id
 * @returns {boolean} True when the user selected no template (blank / "none" sentinel)
 */
function isNoneOrEmptyTemplateId(id) {
  if (id == null) return true;
  const s = String(id).trim();
  if (!s) return true;
  if (s.toLowerCase() === 'none') return true;
  return false;
}

/**
 * @param {{ displayName?: string, uniqueId?: string } | undefined} template
 * @param {string} templateUniqueId
 * @returns {string} Catalog displayName, or uniqueId when the row exists but has no label, or "None" when the id is not in the catalog
 */
function displayNameForMatch(template, templateUniqueId) {
  if (!template) {
    return NONE_LABEL;
  }
  const raw = template.displayName;
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim();
  }
  return String(template.uniqueId || templateUniqueId);
}

/**
 * Hook to get template display name from uniqueId
 * Fetches template records once and caches them
 */
export function useTemplateDisplayName(templateUniqueId) {
  const [displayName, setDisplayName] = useState(
    isNoneOrEmptyTemplateId(templateUniqueId) ? NONE_LABEL : null
  );

  useEffect(() => {
    if (isNoneOrEmptyTemplateId(templateUniqueId)) {
      setDisplayName(NONE_LABEL);
      return;
    }

    const id = String(templateUniqueId);

    // If we have cached data, use it immediately
    if (templateCache) {
      const template = templateCache.find((t) => t.uniqueId === id);
      setDisplayName(displayNameForMatch(template, id));
      return;
    }

    // If a fetch is already in progress, wait for it
    if (templateCachePromise) {
      templateCachePromise.then(() => {
        const template = templateCache?.find((t) => t.uniqueId === id);
        setDisplayName(displayNameForMatch(template, id));
      });
      return;
    }

    // Fetch template records
    templateCachePromise = fetch('/api/template-records', {
      credentials: 'include',
    })
      .then((res) => res.text())
      .then((raw) => {
        const data = raw ? JSON.parse(raw) : {};
        if (data.success && Array.isArray(data.records)) {
          templateCache = data.records;
          const template = templateCache.find((t) => t.uniqueId === id);
          return displayNameForMatch(template, id);
        }
        return NONE_LABEL;
      })
      .catch((err) => {
        console.error('[useTemplateDisplayName] Failed to fetch templates', err);
        return NONE_LABEL;
      })
      .finally(() => {
        templateCachePromise = null;
      });

    templateCachePromise.then((name) => {
      setDisplayName(name);
    });
  }, [templateUniqueId]);

  if (isNoneOrEmptyTemplateId(templateUniqueId)) {
    return NONE_LABEL;
  }
  // While loading, keep showing the raw id (prior behavior) until the catalog resolves
  if (displayName == null) {
    return String(templateUniqueId);
  }
  return displayName;
}
